import { defineElement, FC, useElement, useListen, useProp } from '@atomify/hooks';
import { h } from '@atomify/jsx';
import { CheckboxElement } from '@atoms/form-elements/checkbox/checkbox';
import {
    compareConstructionNumberAdd,
    compareConstructionNumberRemove,
    compareStore,
} from '@store/compare';
import { translateItem, useStylesheet, useTranslate } from '@utilities/hooks';

import style from './compare-button.scss';

const HIDDEN_CLASS = 'u-hidden';

export const CompareButton: FC = () => {
    useStylesheet(style);

    const [id] = useProp<string>('id');
    const compareInput = useElement<HTMLElement>('[js-hook-compare-button-checkbox]');
    const compareLabel = useElement<HTMLElement>('bpd-input span');
    const compareLink = useElement<HTMLElement>('[js-hook-compare-button-link]');

    useListen(compareInput, 'change', compareChanged);

    async function compareChanged(e: Event) {
        const isChecked = (e.currentTarget as HTMLInputElement).checked;

        if (isChecked) {
            compareConstructionNumberAdd(id);
            compareLabel.current!.innerHTML = await translateItem('Compare.Added');
            compareLink.current!.classList.remove(HIDDEN_CLASS);
        } else {
            compareConstructionNumberRemove(id);
            compareLabel.current!.innerHTML = await translateItem('Compare.Compare');
            compareLink.current!.classList.add(HIDDEN_CLASS);
        }
    }

    const store = compareStore.getState().constructionNumberIds;
    const isInStore = store.includes(id);

    return (
        <div class="c-compare-button">
            <CheckboxElement
                label={isInStore ? useTranslate('Compare.Added') : useTranslate('Compare.Compare')}
                name="compare"
                id={`compare-${id}`}
                classes="compare-button__checkbox"
                checked={isInStore}
                hook="compare-button-checkbox"
            />
            <a
                class={`compare-button__button u-content-link${
                    isInStore ? '' : ` ${HIDDEN_CLASS}`
                }`}
                href={window.__ENVIRONMENT_DETAILS__.compareUrl}
                js-hook-compare-button-link>
                {useTranslate('Compare.Check')}
            </a>
        </div>
    );
};

CompareButton.props = {
    id: {
        type: String,
        required: true,
    },
};

defineElement('compare-button', CompareButton);
